/**
 * Slow-request signal — the "the server is waking up" detector.
 *
 * WHY THIS EXISTS
 * ---------------
 * The api runs on Cloud Run with no minimum instance, so when nobody has used
 * it for a while Google shuts the container down entirely. The next request
 * has to boot a fresh one: measured at ~29 seconds on prod, against ~2 seconds
 * warm. During those 29 seconds the app shows a disabled button and no
 * explanation, which is indistinguishable from being broken — Nafis reported
 * exactly that as "the quiz is stuck".
 *
 * We cannot make the wait short without paying for an always-warm instance.
 * We can stop it looking like a fault. This tracks in-flight requests and, once
 * one has been outstanding past the threshold, flips a flag the UI can explain
 * itself with. It stays true for however long the wake takes — no timeout, no
 * cap — because lying about the length is what caused the problem.
 *
 * SLOW IS NOT THE SAME AS ASLEEP (#598). The first version raised the flag for
 * ANY request past the threshold. An AI answer routinely takes longer than
 * that, so every long AI call covered the whole screen with "waking the server
 * up" while the server was plainly awake, and Ishraf could not keep working.
 * A cold start has a signature a slow request does not: Cloud Run holds every
 * request until the container is ready, so NOTHING has come back from the
 * server for a while. So the flag now also needs "no response in the last
 * {@link AWAKE_WINDOW_MS}". A long request on a server that answered a minute
 * ago is just a long request.
 *
 * ASK BEFORE NAMING A CAUSE (#621). Even with that rule the notice said
 * "waking the server up" on a fresh page whose first request hung, and on
 * 20 Sep that was false: the server was awake and a request was stuck behind
 * the database for five minutes, while a real start-up was measured at 3 to 4
 * seconds. Nothing on the client can tell those apart by waiting. So once the
 * flag is up, an optional probe asks the server a question that needs no
 * database, and the answer sets the {@link SlowRequestReason}:
 *   - it answered inside {@link AWAKE_PROBE_TIMEOUT_MS} → `'busy'`: the server
 *     is up, and what is slow is on our side;
 *   - it did not → `'unreachable'`: starting up, or the connection is slow; we
 *     cannot say which, so the words must not pick one.
 * Until the first answer the reason is `'checking'`. The probe repeats every
 * {@link REPROBE_INTERVAL_MS} while the flag stays up, because a wait that
 * began as a start-up can become a stuck request.
 *
 * Deliberately framework-free: this file is imported by the link chain, which
 * runs in every app and in tests. React binding lives in `react/`.
 */

export type SlowRequestListener = (slow: boolean) => void;

/** Why the flag is up, as far as the probe can tell. See the file comment. */
export type SlowRequestReason = 'checking' | 'busy' | 'unreachable';

/**
 * Asks the server whether it is up, with a question that needs no database.
 * Resolves true only for an answer from the API itself; false or a rejection
 * for anything else. Must stop when `signal` aborts.
 */
export type AwakeProbe = (signal: AbortSignal) => Promise<boolean>;

/**
 * How long a request may run before we assume something is wrong rather than
 * merely busy. Comfortably past a warm p99 (~2s) so ordinary slowness never
 * trips it, and far short of the ~29s cold start so the explanation arrives
 * while the user is still wondering rather than after they have given up.
 */
export const SLOW_REQUEST_THRESHOLD_MS = 4000;

/**
 * A server that answered within this window is awake, whatever is slow now.
 * Cloud Run keeps an idle instance for several minutes before scaling to
 * zero, so five minutes errs toward calling a real cold start by its name.
 */
export const AWAKE_WINDOW_MS = 5 * 60_000;

/**
 * An answer to the probe inside this is a server that is up. A start-up was
 * measured at 3 to 4 seconds (#621, 20 Sep), so a server that is booting cannot
 * answer this fast, while one stuck behind its database answers in a few
 * hundred milliseconds because the probe never touches the database.
 */
export const AWAKE_PROBE_TIMEOUT_MS = 2000;

/** While the flag stays up, ask again this often. */
export const REPROBE_INTERVAL_MS = 10_000;

const listeners = new Set<SlowRequestListener>();
let inFlight = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let slow = false;
/** When the server last sent a response, of any status. Null until the first. */
let lastResponseAt: number | null = null;
let reason: SlowRequestReason | null = null;
let probe: AwakeProbe | null = null;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
/** Bumped whenever the flag clears, so a late probe answer cannot reopen it. */
let probeRound = 0;

function answeredRecently(now: number): boolean {
  return lastResponseAt !== null && now - lastResponseAt < AWAKE_WINDOW_MS;
}

function emit(): void {
  for (const listener of listeners) listener(slow);
}

/** Current state — for a listener that subscribes mid-wait. */
export function isRequestSlow(): boolean {
  return slow;
}

/** Why the flag is up; null while it is down. Listeners fire when it changes. */
export function slowRequestReason(): SlowRequestReason | null {
  return reason;
}

/**
 * Install the probe (the link chain does, with the API's `/_alive` route).
 * Without one the reason is always `'unreachable'`, which claims nothing.
 */
export function setAwakeProbe(next: AwakeProbe | null): void {
  probe = next;
}

function setReason(next: SlowRequestReason): void {
  if (reason === next) return;
  reason = next;
  emit();
}

/** Ask once, and schedule the next ask while the flag stays up. */
function runProbe(): void {
  probeTimer = null;
  const ask = probe;
  if (!slow || ask === null) return;
  const round = probeRound;
  const controller = new AbortController();
  const answer = new Promise<boolean>((resolve) => {
    const deadline = setTimeout(() => {
      controller.abort();
      resolve(false);
    }, AWAKE_PROBE_TIMEOUT_MS);
    ask(controller.signal).then(
      (awake) => {
        clearTimeout(deadline);
        resolve(awake);
      },
      () => {
        clearTimeout(deadline);
        resolve(false);
      },
    );
  });
  void answer.then((awake) => {
    if (round !== probeRound || !slow) return;
    setReason(awake ? 'busy' : 'unreachable');
  });
  probeTimer = setTimeout(runProbe, REPROBE_INTERVAL_MS);
}

function stopProbing(): void {
  probeRound += 1;
  if (probeTimer !== null) {
    clearTimeout(probeTimer);
    probeTimer = null;
  }
  reason = null;
}

/** Subscribe to the signal. Fires immediately with the current value. */
export function subscribeSlowRequest(listener: SlowRequestListener): () => void {
  listeners.add(listener);
  listener(slow);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Mark a request as started. Returns the settle callback — call it exactly
 * once, on success OR failure, or the notice would never come down. Pass
 * `{ responded: true }` when the server sent a response (any status): that is
 * the evidence it is awake. A network failure proves nothing, so it passes
 * nothing.
 *
 * The timer is armed only when the first request goes out and cleared when the
 * last one settles, so a steady stream of fast calls never arms it.
 */
export function markRequestStart(): (outcome?: { responded: boolean }) => void {
  inFlight += 1;
  if (inFlight === 1 && timer === null) {
    timer = setTimeout(() => {
      timer = null;
      // Only meaningful if something is still outstanding, and only a cold
      // start if the server has not answered anything lately.
      if (inFlight > 0 && !slow && !answeredRecently(Date.now())) {
        slow = true;
        reason = probe === null ? 'unreachable' : 'checking';
        emit();
        runProbe();
      }
    }, SLOW_REQUEST_THRESHOLD_MS);
  }

  let settled = false;
  return (outcome) => {
    if (settled) return;
    settled = true;
    if (outcome?.responded) lastResponseAt = Date.now();
    inFlight = Math.max(0, inFlight - 1);
    if (inFlight > 0) return;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (slow) {
      slow = false;
      stopProbing();
      emit();
    }
  };
}

/** Test seam: drop all state between cases. */
export function resetSlowRequestState(): void {
  listeners.clear();
  inFlight = 0;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  slow = false;
  lastResponseAt = null;
  stopProbing();
  probe = null;
}
